import { stringify } from "query-string";
import { fetchUtils, DataProvider } from "ra-core";
import { GetListParams, DeleteResult } from "react-admin";
import { debug_log } from "../ra-lb-tools/utils/debugLog";
import { errorHandlingFactory } from "../ra-lb-tools/utils/errors";
import { processQualifiedFilters } from "../ra-lb-tools/utils/api";
import { getValue } from "../ra-lb-tools/utils/tools";

interface CustomHttpClient {
  (url: string, options?: fetchUtils.Options): Promise<any>;
}
/**
 * Maps react-admin queries to the backend REST API
 *
 * getList     => GET http://my.api.url/posts?sort=['title','ASC']&skip=0&limit=24
 * getList     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * getOne      => GET http://my.api.url/posts/123
 * getMany     => GET http://my.api.url/posts?filter={id:[123,456,789]}
 * update      => PUT http://my.api.url/posts/123
 * create      => POST http://my.api.url/posts
 * delete      => DELETE http://my.api.url/posts/123
 *
 */

export const apiDataProvider = (
  apiUrl: string,
  httpClient: CustomHttpClient,
  countHeader = "Content-Range"
): DataProvider => {
  const parseCounterHeader = (headers: Headers): number => {
    if (!headers.has(countHeader)) {
      throw new Error(
        `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`
      );
    }

    const headerValue = headers.get(countHeader.toLowerCase()) as string;

    return countHeader === "Content-Range"
      ? parseInt(headerValue.split("/").pop() as string, 10)
      : parseInt(headerValue, 10);
  };

  const buildOptions = (
    resource: string,
    rangeStart: number,
    rangeEnd: number
  ): object => {
    return countHeader === "Content-Range"
      ? {
          // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
          headers: new Headers({
            Range: `${resource}=${rangeStart}-${rangeEnd}`,
          }),
        }
      : {};
  };

  const customResources: any = {
    __lockerState: {
      parentResource: "lockers",
      parentIdField: "lockerId",
      childResource: "states",
    },
    __organization_members: {
      parentResource: "organizations",
      parentIdField: "organizationId",
      childResource: "members",
      childIdField: "userId",
    },
    __setupStep: {
      parentResource: "tower-setup-plans",
      parentIdField: "setupPlanId",
      childResource: "setup-steps",
    },
  };

  const sortAlias: Record<string, string> = {
    _id: "id",
  };

  const customMeta: any = {
    getMany: {
      towers: {
        status_id: 0,
      },
    },
  };

  /**
   * Current behaviour: built in List component provides resource entity name (i.e. towers, events, etc)
   * and we use it to build api endpoints with standard template
   * Problem: we do not provide standard endpoints for some entities (i.e /events),
   * we only allow access through other enpoints (i.e. towers/events)
   *
   * Solution: we specify such entities and through which entities we should access the data
   * and this method makes the translation
   *
   * @param resource
   * @param params
   * @returns resource
   */
  const processCustomResources = (
    resource: string,
    params: GetListParams,
    use_child = false
  ): string => {
    const resourceSetup = customResources[resource];
    if (resourceSetup) {
      const { parentResource, parentIdField, childResource, childIdField } =
        resourceSetup;

      const parentId = params.filter[parentIdField];
      delete params.filter[parentIdField];

      resource = `${parentResource}/${parentId}/${childResource}`;

      if (use_child && childIdField) {
        const childId = params.filter[childIdField];
        delete params.filter[childIdField];

        if (childId) {
          resource += `/${childId}`;
        }
      }
    }

    return resource;
  };

  const processRequest = (resource: string, params: GetListParams): string => {
    processQualifiedFilters(resource, params);
    resource = processCustomResources(resource, params);

    return resource;
  };

  return {
    getList: (resource, params) => {
      resource = processRequest(resource, params);

      const meta = getValue(params, "meta", {});
      const {
        namespacedParams = {},
        customQuery = {},
        customSegment,
        transformResponse,
        notify,
        isSuperuser,
      } = meta;
      params = { ...params, ...namespacedParams };

      params.sort.field = sortAlias[params.sort.field] || params.sort.field;
      const { field, order } = params.sort;

      params.pagination.page = meta.page || params.pagination.page;
      const { page, perPage } = params.pagination;
      const rangeStart = (page - 1) * perPage;
      const rangeEnd = page * perPage - 1;

      const query = {
        sort: JSON.stringify([field, order]),
        //range: JSON.stringify([rangeStart, rangeEnd]),
        skip: rangeStart,
        limit: perPage,
        filter: JSON.stringify(params.filter),
        ...customQuery,
      };

      let url = `${apiUrl}/${resource}`;

      if (customSegment) {
        url += "/" + customSegment;
      }

      url = `${url}/?${stringify(query)}`;

      const options = buildOptions(resource, rangeStart, rangeEnd);

      debug_log(`getList: ${apiUrl}/${resource}`);
      debug_log(query);
      debug_log(options);

      const onSuccess = (data: { json: any; headers: Headers }) => {
        const res = {
          data: transformResponse
            ? data.json.map(transformResponse)
            : data.json,
          total: parseCounterHeader(data.headers),
        };

        debug_log(`Response: ${apiUrl}/${resource}`);
        debug_log(query);
        debug_log(res);

        return res;
      };

      const promise = httpClient(url, options).then(onSuccess);

      if (notify) {
        const onError = (err: any) => {
          const handler = errorHandlingFactory(notify, isSuperuser);
          handler(err);

          return {
            data: [{ id: "err" }],
            total: 1,
          };
        };

        return promise.catch(onError);
      }

      return promise;
    },

    getOne: (resource, params) => {
      let url = `${apiUrl}/${resource}/${params.id}`;

      const customSegment = getValue(params, "meta.customSegment");
      if (customSegment) {
        url += "/" + customSegment;
      }

      debug_log(`getOne: ${url}`);
      debug_log(params);

      const transformResponse = getValue(params, "meta.transformResponse");

      return httpClient(url).then(({ json }) => ({
        data: transformResponse ? transformResponse(json) : json,
      }));
    },

    getMany: (resource, params) => {
      const paramsMeta = params.meta || {};
      const { target, ...restMeta } = paramsMeta;
      const myCustomMeta = getValue(customMeta, `getMany.${resource}`, {});

      const filterMeta = Object.keys(restMeta).length ? restMeta : myCustomMeta;

      const idField = target || "id";
      const filter = { [idField]: params.ids };
      const query = {
        filter: JSON.stringify({ ...filterMeta, ...filter }),
      };
      const url = `${apiUrl}/${resource}/?${stringify(query)}`;

      debug_log(`getMany: ${apiUrl}/${resource}`);
      debug_log(query);

      return httpClient(url).then(({ json }) => ({ data: json }));
    },

    getManyReference: (resource, params) => {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;

      const rangeStart = (page - 1) * perPage;
      const rangeEnd = page * perPage - 1;

      const query = {
        sort: JSON.stringify([field, order]),
        // range: JSON.stringify([rangeStart, rangeEnd]),
        skip: rangeStart,
        limit: perPage,
        filter: JSON.stringify({
          ...params.filter,
          [params.target]: params.id,
        }),
      };
      const url = `${apiUrl}/${resource}?${stringify(query)}`;
      const options = buildOptions(resource, rangeStart, rangeEnd);

      debug_log(`getManyReference: ${apiUrl}/${resource}`);
      debug_log(query);

      return httpClient(url, options).then(({ headers, json }) => {
        return {
          data: json,
          total: parseCounterHeader(headers),
        };
      });
    },

    update: (resource, params) => {
      let uri = `${apiUrl}/${resource}/${params.id}`;

      const customSegment = getValue(params, "meta.customSegment");
      if (customSegment) {
        uri += "/" + customSegment;
      }

      const method = getValue(params, "meta.customMethod", "PUT");
      const transformResponse = getValue(params, "meta.transformResponse");

      return httpClient(uri, {
        method,
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: transformResponse ? transformResponse(json) : json,
      }));
    },

    updateMany: (resource, params) =>
      Promise.all(
        params.ids.map((id) => {
          let uri = `${apiUrl}/${resource}/${id}`;

          const { __meta, ...data } = params.data;
          const meta = params.meta || __meta;
          const customSegment = meta && meta.customSegment;
          if (customSegment) {
            uri += "/" + customSegment;
          }

          return httpClient(uri, {
            method: "PUT",
            body: JSON.stringify(data),
          });
        })
      ).then((responses) => ({ data: responses.map(({ json }) => json.id) })),

    create: (resource, params) => {
      const customSegment = getValue(params, "meta.customSegment", "");
      const transform = getValue(params, "meta.transform");

      let data = null;
      try {
        data = transform ? transform(params.data) : params.data;
      } catch (error) {
        return Promise.reject(error);
      }
      const body = JSON.stringify(data);

      const fakeParams: GetListParams = {
        filter: { ...data },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "fake", order: "fake" },
      };
      resource = processCustomResources(resource, fakeParams);

      const uri = `${apiUrl}/${resource}/${customSegment}`;

      return httpClient(uri, {
        method: "POST",
        body,
      }).then(({ json }) => ({
        data: { ...params.data, id: getValue(json, "id"), _response: json },
      }));
    },

    delete: (resource, params) => {
      const fakeParams: GetListParams = {
        filter: { ...params.previousData },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "fake", order: "fake" },
      };

      let path = processCustomResources(resource, fakeParams, true);
      const customSegment = getValue(params, "meta.customSegment", "");

      if (path == resource) {
        if (customSegment) {
          path = `${resource}/${customSegment}`;
        } else {
          path = `${resource}/${params.id}`;
        }
      }

      const uri = `${apiUrl}/${path}`;
      const options = {
        method: "DELETE",
        headers: new Headers({
          "Content-Type": "text/plain",
        }),
      };

      const notify = params.meta && params.meta.notify;
      if (notify) {
        const onError = (err: any) => {
          const handler = errorHandlingFactory(notify);
          handler(err);

          const res: DeleteResult = { data: { id: -1 } };
          return res;
        };
        return httpClient(uri, options)
          .then(({ json }) => ({ data: json }))
          .catch(onError);
      } else {
        return httpClient(uri, options).then(({ json }) => ({ data: json }));
      }
    },

    deleteMany: (resource, params) =>
      Promise.all(
        params.ids.map((id) =>
          httpClient(`${apiUrl}/${resource}/${id}`, {
            method: "DELETE",
            headers: new Headers({
              "Content-Type": "text/plain",
            }),
          })
        )
      ).then((responses) => ({
        data: responses.map(({ json }) => json.id),
      })),

    customCommand: (
      url: string,
      notify: (msg: string, config: object) => void,
      method = "POST",
      successMessage = "Success!"
    ) => {
      const onError = errorHandlingFactory(notify);
      if (!url.startsWith("/")) {
        url = "/" + url;
      }

      const uri = apiUrl + url;

      return httpClient(uri, {
        method,
      })
        .then((r) => {
          notify(successMessage, {
            type: "success",
          });

          return getValue(r, "json");
        })
        .catch((err) => onError(err));
    },

    resetEvents: (
      towerId: string,
      reason: string | null,
      notify: (msg: string, config: object) => void,
      resource = "towers"
    ) =>
      httpClient(`${apiUrl}/${resource}/${towerId}/reset-events`, {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
      })
        .then(() =>
          notify(`Events successfully reset on Tower ${towerId}`, {
            type: "success",
          })
        )
        .catch((err) =>
          notify(`Error resetting events: ${err}`, { type: "error" })
        ),

    importRemoteLockers: (
      federationId: number,
      notify: (msg: string, config: object) => void
    ) =>
      httpClient(`${apiUrl}/federations/${federationId}/import-lockers`, {
        method: "POST",
      })
        .then(() =>
          notify(`Lockers successfully import for Federation ${federationId}`, {
            type: "success",
          })
        )
        .catch((err) =>
          notify(`Error importing remote Lockers: ${err}`, { type: "error" })
        ),

    regenerateSecret: (clientId: string) =>
      httpClient(`${apiUrl}/clients/${clientId}/regenerate-secret`, {
        method: "POST",
      }).then(({ json }) => ({
        data: json,
      })),

    retrieveMapsKey: () =>
      httpClient(`${apiUrl}/api-keys/google-maps`, {
        method: "GET",
      }).then(({ json }) => ({
        data: json,
      })),

    retrieveLockerStatuses: () =>
      httpClient(`${apiUrl}/locker-statuses/`, {
        method: "GET",
      }).then(({ json }) => ({
        data: json,
      })),

    addPriceToPlan: (
      planId: string,
      priceId: string,
      notify: (msg: string, config: object) => void
    ) =>
      httpClient(`${apiUrl}/plans/${planId}/add-price`, {
        method: "POST",
        body: JSON.stringify({ priceId }),
      })
        .then(() =>
          notify(`Price successfully added to Plan ${planId}`, {
            type: "success",
          })
        )
        .catch((err) =>
          notify(`Error adding price: ${err}`, { type: "error" })
        ),
  };
};
